import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import DescriptionIcon from "@mui/icons-material/Description";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import axios from "axios";

import generatePaintingPPT from "./generatePaintingPPT";

// Pure light theme with modern Sky Blue and corporate Navy Blue hierarchies
const COLORS = {
  primary: "#0F172A", // Slate Navy for high-contrast legible headings
  skyCore: "#0EA5E9", // Primary Sky Blue accent
  skyLight: "#F0F9FF", // Pure light sky blue tint for cards and counters
  skyBorder: "#E0F2FE", // Soft blue-tinted borders
  bgLight: "#FAFAFA", // Matte studio white backdrop
  border: "#E2E8F0", // Neutral concrete layout split lines
  textMuted: "#64748B", // Secondary information prose grey
  white: "#FFFFFF", // True crisp absolute white
};

export default function PaintingReportModal({
  open,
  onClose,
  CampaignDetails,
}) {
  const [paintingJobs, setPaintingJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    if (currentIndex < paintingJobs.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [currentIndex, paintingJobs]);

  const fetchAllPaintingJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/vendorClusterBatches/getAllClusterBatches?campaignId=${CampaignDetails?.data?._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const clusters = response?.data?.data || [];
      const jobs = clusters
        ?.filter((cluster) => cluster?.serviceType === "3")
        ?.flatMap((cluster) => cluster?.batches || [])
        ?.flatMap((batch) => batch?.jobs || [])
        ?.filter((job) => job?.status === 2);

      setPaintingJobs(jobs);
    } catch (error) {
      console.error("Error fetching painting jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && CampaignDetails?.data?._id) {
      fetchAllPaintingJobs();
    }
  }, [open, CampaignDetails]);

  const [downloading, setDownloading] = useState(false);
  const campaignTitle =
    CampaignDetails?.data?.campaignTitle || "Untitled Campaign";

  const handleDownloadAll = async () => {
    try {
      setDownloading(true);
      await generatePaintingPPT({ CampaignDetails, jobs: paintingJobs });
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          background: COLORS.white,
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      {/* GLOBAL CONTROLLER MODAL HEADER */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.white,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 4,
              height: 20,
              backgroundColor: COLORS.skyCore,
              borderRadius: "4px",
            }}
          />
          <Typography
            sx={{
              color: COLORS.primary,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1.5,
            }}
          >
            PAINTING REPORT EXPORT MANAGER
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={downloading}
            onClick={handleDownloadAll}
            sx={{
              borderColor: COLORS.border,
              color: COLORS.primary,
              borderRadius: "8px",
              textTransform: "none",
              px: 2.5,
              py: 0.75,
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: COLORS.white,
              "&:hover": {
                borderColor: COLORS.skyCore,
                backgroundColor: COLORS.skyLight,
                color: COLORS.skyCore,
              },
            }}
          >
            {downloading
              ? "Generating Package..."
              : "Download Presentation (.PPT)"}
          </Button>

          <IconButton
            onClick={onClose}
            sx={{
              border: `1px solid ${COLORS.border}`,
              borderRadius: "8px",
              p: 0.75,
              color: COLORS.textMuted,
              "&:hover": {
                color: COLORS.primary,
                backgroundColor: COLORS.bgLight,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* CORE VIEWPORT BODY */}
      <DialogContent sx={{ p: 0, backgroundColor: COLORS.bgLight }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="500px"
          >
            <Typography fontSize={14} fontWeight={500} color={COLORS.textMuted}>
              Fetching asset pipeline documents...
            </Typography>
          </Box>
        ) : paintingJobs.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="500px"
          >
            <Typography fontSize={14} color={COLORS.textMuted}>
              No confirmed completed painting logs registered.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", position: "relative", p: 2 }}>
            {/* ACTION PAGINATION NAVIGATION LAYER */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: 16,
                right: 16,
                transform: "translateY(-50%)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pointerEvents: "none",
                zIndex: 10,
              }}
            >
              <IconButton
                onClick={prevSlide}
                disabled={currentIndex === 0}
                sx={{
                  pointerEvents: "auto",
                  backgroundColor: COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
                  p: 1.5,
                  "&:hover": {
                    borderColor: COLORS.skyCore,
                    color: COLORS.skyCore,
                    backgroundColor: COLORS.skyLight,
                  },
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <IconButton
                onClick={nextSlide}
                disabled={currentIndex === paintingJobs.length - 1}
                sx={{
                  pointerEvents: "auto",
                  backgroundColor: COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
                  p: 1.5,
                  "&:hover": {
                    borderColor: COLORS.skyCore,
                    color: COLORS.skyCore,
                    backgroundColor: COLORS.skyLight,
                  },
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* PRESENTATION CONTAINER OBJECT */}
            {paintingJobs
              .slice(currentIndex, currentIndex + 1)
              .map((jobData, index) => {
                const painting = jobData?.paintingJobRelatedFields || {};
                const images = (painting?.paintingImages || []).slice(0, 2);

                return (
                  <Card
                    key={jobData?._id || index}
                    elevation={0}
                    sx={{
                      margin: "0 auto",
                      borderRadius: "12px",
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      overflow: "hidden",
                    }}
                  >
                    {/* CANVASS TITLE STRIP */}
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: `1px solid ${COLORS.border}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        backgroundColor: COLORS.white,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: COLORS.skyCore,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            mb: 0.5,
                          }}
                        >
                          CAMPAIGN BRANDING PREVIEW
                        </Typography>
                        <Typography
                          sx={{
                            color: COLORS.primary,
                            fontSize: 20,
                            fontWeight: 700,
                          }}
                        >
                          {campaignTitle}
                        </Typography>
                      </Box>

                      {/* SKY BLUE LAYER SLIDE TRACKER INDEX */}
                      <Box
                        sx={{
                          backgroundColor: COLORS.skyLight,
                          px: 2,
                          py: 0.5,
                          borderRadius: "20px",
                          border: `1px solid ${COLORS.skyBorder}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: COLORS.skyCore,
                          }}
                        >
                          Item {currentIndex + 1} of {paintingJobs.length}
                        </Typography>
                      </Box>
                    </Box>

                    {/* INTERIOR META GRID METRICS */}
                    <Grid
                      container
                      sx={{
                        borderBottom: `1px solid ${COLORS.border}`,
                        backgroundColor: COLORS.white,
                      }}
                    >
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          p: 2,
                          borderRight: { md: `1px solid ${COLORS.border}` },
                        }}
                      >
                        <MetaBlock
                          label="Store Entity"
                          value={jobData?.storeName || "Unassigned"}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          p: 2,
                          borderRight: { md: `1px solid ${COLORS.border}` },
                        }}
                      >
                        <MetaBlock
                          label="Product Group Type"
                          value={jobData?.productId?.productType || "N/A"}
                        />
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ p: 2 }}>
                        <div>
                          <MetaBlock
                            label="Audited Painting Footprint"
                            value={
                              painting?.totalPaintedArea
                                ? `${jobData?.productId?.widthOfPaintedProduct || "N/A"} X ${jobData?.productId?.heightOfPaintedProduct || "N/A"} = ${painting.totalPaintedArea} SQ.FT`
                                : "N/A"
                            }
                            valueColor={COLORS.skyCore}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    {/* ADDRESS SECTOR SUB-BAR */}
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        borderBottom: `1px solid ${COLORS.border}`,
                        bg: COLORS.bgLight,
                      }}
                    >
                      <LocationOnIcon
                        sx={{ color: COLORS.skyCore, fontSize: 18 }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: COLORS.textMuted,
                            letterSpacing: 0.5,
                          }}
                        >
                          SITE LOCATION COORDINATES
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: COLORS.primary,
                            fontWeight: 500,
                          }}
                        >
                          {[
                            jobData?.address,
                            jobData?.city,
                            jobData?.state,
                            jobData?.zipCode,
                          ]
                            .filter(Boolean)
                            .join(", ") || "No verifiable address logged."}
                        </Typography>
                      </Box>
                    </Box>

                    {/* TWO-UP VISUAL PROOF IMAGE MEDIA BOX */}
                    <Box sx={{ p: 2.5, backgroundColor: COLORS.bgLight }}>
                      <Grid container spacing={2}>
                        {[0, 1].map((imgIndex) => {
                          const image = images?.[imgIndex];
                          return (
                            <Grid item xs={12} sm={6} key={imgIndex}>
                              <Box
                                sx={{
                                  backgroundColor: COLORS.white,
                                  border: `1px solid ${COLORS.border}`,
                                  borderRadius: "8px",
                                  height: "280px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  overflow: "hidden",
                                  position: "relative",
                                }}
                              >
                                {image ? (
                                  <img
                                    loading="lazy"
                                    src={image}
                                    alt={`Inspection Asset View ${imgIndex + 1}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      textAlign: "center",
                                      color: COLORS.textMuted,
                                    }}
                                  >
                                    <ImageIcon
                                      sx={{
                                        fontSize: 36,
                                        color: COLORS.skyCore,
                                        opacity: 0.25,
                                        mb: 1,
                                      }}
                                    />
                                    <Typography
                                      sx={{ fontSize: 13, fontStyle: "italic" }}
                                    >
                                      No Proof Photo Provided
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>

                    {/* VERIFICATION AND COMPLIANCE INTEGRATION FIELDS */}
                    <Grid
                      container
                      sx={{ borderTop: `1px solid ${COLORS.border}` }}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        sx={{
                          borderRight: { sm: `1px solid ${COLORS.border}` },
                        }}
                      >
                        <BottomSection
                          title="COMPLIANCE RECORDING (VIDEO)"
                          icon={<VideocamIcon fontSize="small" />}
                          color={COLORS.primary}
                          value={painting?.paintVideo}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <BottomSection
                          title="AUTHORIZED HANDOVER DOCUMENTATION"
                          icon={<DescriptionIcon fontSize="small" />}
                          color={COLORS.skyCore}
                          value={painting?.signedDocument}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                );
              })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* HIGH PERFORMANCE PROP LABELED TEXT WRAPPERS */
function MetaBlock({ label, value, valueColor = COLORS.primary }) {
  return (
    <Box>
      <Typography
        sx={{
          color: COLORS.textMuted,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: valueColor,
          fontSize: 16,
          fontWeight: 700,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* COMPLIANCE DATA DISPLAY UNITS */
function BottomSection({ title, icon, color, value }) {
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        backgroundColor: COLORS.white,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box sx={{ color: color, display: "flex", alignItems: "center" }}>
          {icon}
        </Box>
        <Typography
          sx={{
            color: color,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
          }}
        >
          {title}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: value ? COLORS.primary : COLORS.textMuted,
          fontSize: 12,
          fontWeight: value ? 500 : 400,
          wordBreak: "break-all",
          fontStyle: value ? "normal" : "italic",
          backgroundColor: value ? COLORS.skyLight : COLORS.bgLight,
          p: 1.5,
          borderRadius: "6px",
          border: `1px solid ${value ? COLORS.skyBorder : COLORS.border}`,
        }}
      >
        {value || "No verified file path attachment verified on database."}
      </Typography>
    </Box>
  );
}
